import { ReactNode, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useUsersApi } from '../services/usersApi'
import UserMenu from './UserMenu'
import {
  HomeIcon,
  UserGroupIcon,
  CalendarIcon,
  TrophyIcon,
  MapPinIcon,
  CogIcon,
  ArchiveBoxIcon,
  RectangleGroupIcon,
  Bars3Icon,
  XMarkIcon,
  BeakerIcon,
  FlagIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { isPoolAdmin } from '../utils/roles'
import ThemeToggle from './ThemeToggle'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user: auth0User, logout } = useAuth0()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const usersApi = useUsersApi()

  // Fetch current user data to get custom avatar
  const { data: userData } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => usersApi.getCurrentUser(),
    enabled: !!auth0User, // Only run query when Auth0 user is available
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  const roles = (auth0User && (auth0User['https://midwestrailbirds.com/roles'] as string[])) || []
  const isAdmin = roles.includes('admin')
  const userIsPoolAdmin = isPoolAdmin(auth0User)

  // Use custom avatar if available, otherwise fall back to Auth0 picture
  const avatarUrl = userData?.avatar || auth0User?.picture
  
  // Debug: uncomment to troubleshoot navigation
  // console.log('Layout Navigation Debug:', { auth0User, roles, isAdmin, userIsPoolAdmin })
  
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Members', href: '/members', icon: UserGroupIcon },
    { name: 'Events', href: '/events', icon: CalendarIcon },
    { name: 'Pools', href: '/pools', icon: TrophyIcon },
    { name: 'Golf', href: '/golf/outings', icon: FlagIcon },
    { name: 'Chat', href: '/chat', icon: ChatBubbleLeftRightIcon },
    { name: 'Bourbon Swap', href: '/bourbon', icon: BeakerIcon },
    { name: 'Legacy', href: '/legacy', icon: ArchiveBoxIcon },
    ...(isAdmin ? [
      { name: 'Locations', href: '/locations', icon: MapPinIcon },
      { name: 'Admin', href: '/admin', icon: CogIcon }
    ] : [])
  ]

  const handleLogout = async () => {
    const returnTo = import.meta.env.VITE_AUTH0_LOGOUT_REDIRECT_URI || window.location.origin
    await logout({ logoutParams: { returnTo } })
  }

  const isChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/')

  return (
    <div className={`min-h-screen bg-rb-dark ${isChatRoute ? 'h-dvh flex flex-col overflow-hidden' : ''}`}>
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                Midwest Railbirds
              </Link>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                      isActive
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                    }`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500"
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* User menu */}
            <div className="hidden md:flex items-center space-x-4 relative z-50">
              <ThemeToggle />
              
              {auth0User && <UserMenu />}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                      isActive
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                )
              })}
              
              {/* Mobile user menu items */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center space-x-3">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={auth0User?.name || 'User avatar'}
                        className="h-8 w-8 rounded-full object-cover"
                        onError={(e) => {
                          // If custom avatar fails to load, fall back to Auth0 picture
                          if (userData?.avatar && e.currentTarget.src === userData.avatar) {
                            e.currentTarget.src = auth0User?.picture || ''
                          }
                        }}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                          {((auth0User as any)?.given_name || (auth0User as any)?.name || (auth0User as any)?.email || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="text-base font-medium text-gray-800 dark:text-gray-200">
                        {(auth0User as any)?.given_name || (auth0User as any)?.name || (auth0User as any)?.email}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                        {(auth0User && Array.isArray(auth0User['https://midwestrailbirds.com/permissions']) && auth0User['https://midwestrailbirds.com/permissions'].includes('admin')) ? 'Admin' : 'Member'}
                      </div>
                    </div>
                  </div>
                  <ThemeToggle />
                </div>
                
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white transition-colors duration-200"
                >
                  <span>View Profile</span>
                </Link>
                
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    handleLogout()
                  }}
                  className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900 dark:hover:text-red-300 transition-colors duration-200"
                >
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className={`w-full ${isChatRoute ? 'flex-1 min-h-0 overflow-hidden' : ''}`}>
        {children}
      </main>
    </div>
  )
}
